// Copy-to-clipboard with a transient "copied" flag for the check-icon swap.
// The failure toast is shared so every copy affordance degrades the same way
// (there's no flash/execCommand fallback under the strict CSP — the user is
// told to select and copy by hand).

import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

export function useCopy(timeoutMs = 1500): {
  copied: boolean;
  /** Resolves true when the text landed on the clipboard. */
  copy: (text: string) => Promise<boolean>;
} {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = useCallback(
    async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(false), timeoutMs);
        return true;
      } catch {
        toast.error("Couldn't copy — select and copy manually");
        return false;
      }
    },
    [timeoutMs],
  );

  return { copied, copy };
}
